"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useLocalStorage = useLocalStorage;
const react_1 = require("react");
/**
 * Hook to persist state in localStorage with SSR support.
 * Handles JSON serialization/deserialization automatically.
 *
 * @param key - The localStorage key
 * @param initialValue - The initial value if nothing is stored
 * @returns Tuple of [value, setValue, removeValue]
 *
 * @example
 * ```tsx
 * function ThemeSwitcher() {
 *   const [theme, setTheme, removeTheme] = useLocalStorage('theme', 'light');
 *
 *   return (
 *     <button onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')}>
 *       Current: {theme}
 *     </button>
 *   );
 * }
 * ```
 */
function useLocalStorage(key, initialValue) {
    // State to store our value
    const [storedValue, setStoredValue] = (0, react_1.useState)(initialValue);
    const [isInitialized, setIsInitialized] = (0, react_1.useState)(false);
    // Initialize from localStorage after mount (SSR-safe)
    (0, react_1.useEffect)(() => {
        if (typeof window === 'undefined')
            return;
        try {
            const item = window.localStorage.getItem(key);
            if (item) {
                setStoredValue(JSON.parse(item));
            }
        }
        catch (error) {
            console.warn(`Error reading localStorage key "${key}":`, error);
        }
        setIsInitialized(true);
    }, [key]);
    // Return a wrapped version of useState's setter function that persists to localStorage
    const setValue = (0, react_1.useCallback)((value) => {
        try {
            // Allow value to be a function for same API as useState
            const valueToStore = value instanceof Function ? value(storedValue) : value;
            setStoredValue(valueToStore);
            if (typeof window !== 'undefined') {
                window.localStorage.setItem(key, JSON.stringify(valueToStore));
            }
        }
        catch (error) {
            console.warn(`Error setting localStorage key "${key}":`, error);
        }
    }, [key, storedValue]);
    // Function to remove the item from localStorage
    const removeValue = (0, react_1.useCallback)(() => {
        try {
            setStoredValue(initialValue);
            if (typeof window !== 'undefined') {
                window.localStorage.removeItem(key);
            }
        }
        catch (error) {
            console.warn(`Error removing localStorage key "${key}":`, error);
        }
    }, [key, initialValue]);
    return [storedValue, setValue, removeValue];
}
